import './like-condition.css';

import h from 'hyperscript';
import isEqual from 'lodash-es/isEqual';
import searchIcon from '@disy/cadenza-icons/search.svg';

import { Select } from 'ui/select/select';
import { createIconButton } from 'ui/button/button';
import { setupPopupButton } from 'ui/popup/popup';

import { icon } from 'cadenza/utils/icon/icon';
import { LikeConditionApi } from 'cadenza/api-client/conditions/like-condition-api';

import { ConditionElement } from '../condition-element';

import i18n from './messages.properties';
import { DEFAULT_CASESENSITIVITY, DEFAULT_COMBINATIONTYPE, DEFAULT_FILTERTYPE, LikeConditionDropdown } from './like-condition-dropdown';

const name = 'd-like-condition';

export class LikeCondition extends ConditionElement {

  _conditionApiSupplier;
  _selectInput;
  #dropdownButton;
  #latestSetValuesPromise; // for keeping consistency for other conditions, see onConditionChange
  _filterType;
  _caseSensitivity;
  _combinationType;
  _value;

  constructor ({
    conditionApiSupplier = configObject => new LikeConditionApi(configObject),
    filterType = DEFAULT_FILTERTYPE,
    caseSensitivity = DEFAULT_CASESENSITIVITY,
    combinationType = DEFAULT_COMBINATIONTYPE,
    values
  } = {}) {
    super();
    this._conditionApiSupplier = conditionApiSupplier;
    this._filterType = filterType;
    this._caseSensitivity = caseSensitivity;
    this._combinationType = combinationType;
    this._value = values?.valueItems || [];
  }

  connectedCallback () {
    this.classList.add(name);
    this.#dropdownButton = createIconButton(
      icon(searchIcon),
      i18n('dialog-trigger.title'),
      { dropdown: true, variant: 'borderless', styleClass: 'd-like-condition--filter-type-button' });
    let dropdown;
    setupPopupButton(
      this.#dropdownButton,
      () => {
        dropdown = new LikeConditionDropdown({
          filterType: this.filterType, caseSensitivity: this.caseSensitivity, combinationType: this.combinationType });
        return dropdown;
      },
      {
        onClose: () => {
          this.filterType = dropdown.returnValue.filterType;
          this.caseSensitivity = dropdown.returnValue.caseSensitivity;
          this.combinationType = dropdown.returnValue.combinationType;
          // Previously we only triggered `onConditionChange` when we also had values set.
          // This caused issues when no value was set, as settings wouldn't be persisted and get lost on reload.
          // Always sending the settings will cause some additional (perhaps unnecessary invalidation),
          // but prevents inconsistencies to crop up e. g. when extracting the settings with a report.
          // A bigger fix would be to introduce seperate condition APIs for *only* updating settings
          // (assuming that e. g. in the workbook context `SET_CONDITION_SETTING` won't invalidate,
          // but only `SET_CONDITION` does).
          this.onConditionChange();
        }
      });

    this.conditionApi = this._conditionApiSupplier({
      conditionSetId: this.conditionSetId,
      conditionName: this.conditionName,
      conditionId: this.conditionId,
      conditionValuesSetHashGetter: () => this.conditionValuesSetHash
    });

    this._selectInput = new Select({
      tags: true,
      placeholder: i18n('common:select2.placeholder'),
      dropdownCssClass: 'hidden',
      maxSelection: this.maxSelection, // from parent class
      multiple: true
    });

    this._selectInput.disabled = !this.enabled;
    this.#dropdownButton.disabled = this._selectInput.disabled;
    this._selectInput.addEventListener('change', () => {
      this.onConditionChange();
    });

    const contentBox = h('.table-condition-box');
    contentBox.append(this.#dropdownButton, this._selectInput);
    this.append(contentBox);
    const data = this.previousElementSibling.value;
    if (data) {
      this._filterType = data.value.filterType;
      this._caseSensitivity = data.value.caseSensitivity;
      this._combinationType = data.value.combinationType;
      this.value = data.value.rows.flatMap(row => row.values);
      this.defaultValue = data.defaultValue.rows.flatMap(row => row.values);
    } else {
      this.value = this.prepareRows(this._value);
      this.defaultValue = [];
    }
    this.initChanged();
  }

  prepareRows (rows) {
    return rows.map(row => {
      row.value = row.values;
      row.id = row.values.join();
      row.text = row.id;
      return row;
    });
  }

  initChanged () {
    this.changed = !isEqual(this.value, this.defaultValue);
  }

  get value () {
    return this._selectInput.selection.map(value => value.id);
  }

  set value (value) {
    this._selectInput.selection = value;
  }

  get filterType () {
    return this._filterType;
  }

  set filterType (filterType) {
    this._filterType = filterType;
  }

  get caseSensitivity () {
    return this._caseSensitivity;
  }

  set caseSensitivity (caseSensitivity) {
    this._caseSensitivity = caseSensitivity;
  }

  get combinationType () {
    return this._combinationType;
  }

  set combinationType (combinationType) {
    this._combinationType = combinationType;
  }

  onConditionChange () {
    // the filter controller is not availabe in workbooks
    this.filterController?.disableOtherConditionsIfNeededForConsistency([ this ]);
    this.querySelectorAll('.select2-search-choice').forEach(choice => { choice.setAttribute('title', i18n(`filtertype.${this._filterType.toLowerCase()}`)); });
    const setValuesPromise = this.conditionApi.setValue(this.filterType, this.caseSensitivity, this.combinationType, this.value);
    this.#latestSetValuesPromise = setValuesPromise;
    setValuesPromise
      .then((json) => super.onConditionChange(json))
      .catch(error => this.handleConditionValueChangeError(error))
      .finally(() => {
        // if the changes are not fully processed yet, disable the other conditions again.
        const enable = setValuesPromise === this.#latestSetValuesPromise;
        // the filter controller is not availabe in workbooks
        this.filterController?.setConditionsDisabledForConsistency(enable, this);
      });
  }

  get hasValue () {
    return this.value.length > 0;
  }

}

customElements.define(name, LikeCondition);
